import { z } from 'zod';
import {
  AsYouType,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/min';
import metadata from 'libphonenumber-js/min/metadata';
import { PHONE_E164_PREFIX, PHONE_STRINGS } from '@/lib/constants/phone';

const NON_DIGITS = /\D/g;
/** A value that is already E.164: `+`, a non-zero first digit, 7 to 15 digits, nothing else. */
const E164_SHAPE = /^\+[1-9]\d{6,14}$/;
const MAX_CALLING_CODE_DIGITS = 3;

export interface PhoneParts {
  iso2: CountryCode;
  /** The number without the country code, digits only. */
  nationalNumber: string;
}

/** The form-side shape of a stored phone (see `toPhoneFormFields`). */
export interface PhoneFormFields {
  /** E.164 when the stored value reads as a valid `+` number, otherwise `''`. */
  phone: string;
  /** The stored text when it could not be read as a number, otherwise `''`. Form-only: never sent. */
  legacyPhone: string;
}

function asCountryCode(iso2: string | null | undefined): CountryCode | undefined {
  const upper = iso2?.toUpperCase();
  return upper && isSupportedCountry(upper) ? (upper as CountryCode) : undefined;
}

/** `+1` for `US`; `undefined` for a country the library has no dial code for. */
export function dialCodeFor(iso2: string | null | undefined): string | undefined {
  const country = asCountryCode(iso2);
  return country ? `${PHONE_E164_PREFIX}${getCountryCallingCode(country)}` : undefined;
}

/** Whether `libphonenumber-js` can validate and dial-code this country. */
export function isPhoneCountry(iso2: string | null | undefined): boolean {
  return asCountryCode(iso2) !== undefined;
}

/**
 * Formats national-number digits as the user types them, per the selected country's number
 * format (MYN-301, FR-218) - e.g. a US number shows as `(415) 555-0142` while being entered. A
 * country the library has no format data for, or digits too long/short to match a pattern, fall
 * back to the plain digits unformatted - never throws. Pure: a fresh formatter every call, never
 * fed incrementally, so the result depends only on `iso2`/`digits`, not call order.
 */
export function formatNationalDigits(iso2: string | null | undefined, digits: string): string {
  const country = asCountryCode(iso2);
  return new AsYouType(country).input(digits);
}

/**
 * The main country of a calling code (`+1` is US, `+44` is GB), for a number whose country cannot
 * be told apart from its digits yet. The metadata lists the main country first.
 */
function mainCountryForCallingCode(callingCode: string): CountryCode | undefined {
  return (metadata.country_calling_codes as Record<string, CountryCode[]>)[callingCode]?.[0];
}

type ParsedNumber = NonNullable<ReturnType<typeof parsePhoneNumberFromString>>;

/**
 * Where several countries share a calling code (US/CA, GB/GG), `preferredIso2` wins when it
 * shares the number's code, so reopening a saved number keeps the picker on the user's country.
 */
function partsFromParsed(parsed: ParsedNumber, preferredIso2?: string | null): PhoneParts | undefined {
  const preferred = asCountryCode(preferredIso2);
  const sharesCode = preferred && getCountryCallingCode(preferred) === parsed.countryCallingCode;
  const iso2 =
    (sharesCode ? preferred : undefined) ??
    parsed.country ??
    mainCountryForCallingCode(parsed.countryCallingCode);
  return iso2 ? { iso2, nationalNumber: parsed.nationalNumber } : undefined;
}

/**
 * A number too short for the library to parse (`+44 7`, `+1`) still names its country through its
 * calling code: calling codes are prefix-free, so the first 1 to 3 digits that match one are THE code.
 * The preferred country wins when it shares the code, otherwise the code's main country.
 */
function partsFromCallingCode(text: string, preferredIso2?: string | null): PhoneParts | undefined {
  const digits = text.replace(NON_DIGITS, '');
  const table = metadata.country_calling_codes as Record<string, CountryCode[]>;
  for (let length = 1; length <= MAX_CALLING_CODE_DIGITS; length += 1) {
    const callingCode = digits.slice(0, length);
    const countries = table[callingCode];
    if (!countries) continue;
    const preferred = asCountryCode(preferredIso2);
    const sharesCode = preferred && getCountryCallingCode(preferred) === callingCode;
    const iso2 = (sharesCode ? preferred : undefined) ?? countries[0];
    return { iso2, nationalNumber: digits.slice(length) };
  }
  return undefined;
}

/**
 * Reads a stored value as a VALID `+` number, or `undefined` when it cannot be (legacy free
 * text, a number with no country code, an invalid length).
 */
export function parsePhone(
  value: string | null | undefined,
  preferredIso2?: string | null,
): PhoneParts | undefined {
  const text = value?.trim();
  if (!text || !text.startsWith(PHONE_E164_PREFIX)) return undefined;
  const parsed = parsePhoneNumberFromString(text);
  return parsed?.isValid() ? partsFromParsed(parsed, preferredIso2) : undefined;
}

/**
 * What a typed or pasted `+` number means: its country (picker follows) and national digits.
 * Unlike `parsePhone` this accepts a number that is not valid yet, so the picker still follows
 * a half-typed paste. `undefined` when the text has no readable country code.
 */
export function parsePastedPhone(
  text: string,
  preferredIso2?: string | null,
): PhoneParts | undefined {
  const trimmed = text.trim();
  if (!trimmed.startsWith(PHONE_E164_PREFIX)) return undefined;
  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed) return partsFromParsed(parsed, preferredIso2);
  return partsFromCallingCode(trimmed, preferredIso2);
}

/**
 * The form value for a country and the digits typed beside it: normalized E.164 when valid for
 * that country, otherwise the `+<dial code><digits>` candidate (which `phoneSchema` refuses),
 * or `''` when no digits were typed.
 */
export function composePhone(iso2: string | null | undefined, digits: string): string {
  const national = digits.replace(NON_DIGITS, '');
  if (!national) return '';
  const country = asCountryCode(iso2);
  const parsed = country ? parsePhoneNumberFromString(national, country) : undefined;
  if (parsed?.isValid()) return parsed.number;
  return `${dialCodeFor(country) ?? PHONE_E164_PREFIX}${national}`;
}

/** True when `value` is a valid E.164 number (the only shape the API accepts). */
export function isValidPhone(value: string): boolean {
  return value.startsWith(PHONE_E164_PREFIX) && parsePhoneNumberFromString(value)?.isValid() === true;
}

/**
 * Zod rule for a phone form value (E.164, `''` when empty). A non-empty value must be a valid
 * number ("Enter a valid phone number for the selected country"); an empty one fails only when
 * `required`. A form that can hold a legacy value (`legacyPhone`) passes `required: false` and
 * adds its own "empty and no legacy value" check - see the partner profile schema.
 */
export function phoneSchema(required: true): z.ZodString;
export function phoneSchema(required: false): z.ZodOptional<z.ZodString>;
export function phoneSchema(required: boolean): z.ZodString | z.ZodOptional<z.ZodString>;
export function phoneSchema(required: boolean): z.ZodString | z.ZodOptional<z.ZodString> {
  const base = z.string().trim().superRefine((value, ctx) => {
    if (value === '') {
      if (required) ctx.addIssue({ code: z.ZodIssueCode.custom, message: PHONE_STRINGS.REQUIRED });
      return;
    }
    if (!isValidPhone(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: PHONE_STRINGS.INVALID });
    }
  });
  return required ? base : base.optional();
}

/**
 * Read-view text for a stored phone (FR-184, A-200): a value that is ALREADY E.164 and valid is
 * shown in international format (`+1 415 555 0142`); anything else - legacy free text, or a
 * formatted `+1 (212) 555-0170` even if the library could parse it - is shown exactly as stored.
 * Blank in, blank out.
 */
export function formatPhoneDisplay(value: string): string;
export function formatPhoneDisplay(value: string | null | undefined): string | undefined;
export function formatPhoneDisplay(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (!E164_SHAPE.test(value)) return value;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() ? parsed.formatInternational() : value;
}

/**
 * Splits a stored phone for a form (MYN-277 legacy rules): a valid `+` number fills the picker
 * and input (`phone`, normalized to E.164); any other non-empty text is kept as `legacyPhone`
 * so the form can show it beside the field while `phone` stays empty.
 */
export function toPhoneFormFields(stored: string | null | undefined): PhoneFormFields {
  const text = stored?.trim() ?? '';
  if (!text) return { phone: '', legacyPhone: '' };
  const parts = parsePhone(text);
  if (!parts) return { phone: '', legacyPhone: text };
  const parsed = parsePhoneNumberFromString(text);
  return { phone: parsed?.number ?? '', legacyPhone: '' };
}

/**
 * The `phone` key of a save payload.
 *
 * Without `savedPhone` (the original two-argument form): omitted while the form still shows
 * nothing for a legacy value the user has not replaced, so a legacy phone is neither rejected by
 * the API nor wiped; otherwise the phone as the form holds it (`''` when empty).
 *
 * With `savedPhone` (what is stored today): the backend reads `null` as "clear" and `''` as "not
 * sent", so a phone the user emptied (or a legacy value they removed) is sent as `null`; an empty
 * phone that was never saved sends nothing; an untouched legacy value still sends nothing.
 */
export function phonePayload(
  phone: string | null | undefined,
  legacyPhone: string | null | undefined,
): { phone?: string };
export function phonePayload(
  phone: string | null | undefined,
  legacyPhone: string | null | undefined,
  savedPhone: string | null | undefined,
): { phone?: string | null };
export function phonePayload(
  phone: string | null | undefined,
  legacyPhone: string | null | undefined,
  savedPhone?: string | null,
): { phone?: string | null } {
  const current = phone?.trim() ?? '';
  if (current) return { phone: current };
  if (legacyPhone) return {};
  if (savedPhone === undefined) return { phone: '' };
  return savedPhone?.trim() ? { phone: null } : {};
}
